import React, { useCallback, useRef } from "react";
import { Inbox } from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  Empty,
  EmptyContent,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from "@/components/ui/empty";
import {
  RefreshCCWIcon,
  type RefreshCCWIconHandle,
} from "@/components/ui/refresh-ccw-icon";
import {
  UploadIcon,
  type UploadIconHandle,
} from "@/components/ui/upload-icon";
import { useSidebarResyncTrades } from "@/hooks/useSidebarResyncTrades";

export type TradeLogEmptyStateVariant = "no-trades" | "filter-mismatch";

export interface TradeLogEmptyStateProps {
  variant: TradeLogEmptyStateVariant;
  onImportTrades: () => void;
}

export function TradeLogEmptyState({
  variant,
  onImportTrades,
}: TradeLogEmptyStateProps) {
  const { isResyncing, onResync, showResync } = useSidebarResyncTrades();
  const resyncIconRef = useRef<RefreshCCWIconHandle | null>(null);
  const importIconRef = useRef<UploadIconHandle | null>(null);

  const startResyncIconAnimation = useCallback(() => {
    if (isResyncing) return;
    void resyncIconRef.current?.startAnimation();
  }, [isResyncing]);

  const stopResyncIconAnimation = useCallback(() => {
    void resyncIconRef.current?.stopAnimation();
  }, []);

  const startImportIconAnimation = useCallback(() => {
    void importIconRef.current?.startAnimation();
  }, []);

  const stopImportIconAnimation = useCallback(() => {
    void importIconRef.current?.stopAnimation();
  }, []);

  const isFilterMismatch = variant === "filter-mismatch";

  return (
    <Empty
      data-trade-log-empty=""
      className="min-h-[280px] border-border/80 py-10"
    >
      <EmptyHeader>
        <EmptyMedia variant="icon">
          <Inbox className="size-4 text-muted-foreground dark:text-sidebar-muted-foreground" aria-hidden />
        </EmptyMedia>
        <EmptyTitle className="font-[family-name:var(--font-primary)] text-base font-normal text-foreground">
          {isFilterMismatch ? "No trades match your filters" : "No trades yet"}
        </EmptyTitle>
        <EmptyDescription className="font-[family-name:var(--font-primary)] font-normal text-gray-500 dark:text-sidebar-muted-foreground">
          {isFilterMismatch
            ? "Try adjusting your filters or date range to see more trades."
            : "Sync trades from your connected broker or import a CSV for your active account."}
        </EmptyDescription>
      </EmptyHeader>
      {!isFilterMismatch ? (
        <EmptyContent className="flex-row flex-wrap justify-center gap-2">
          {showResync ? (
          <Button
            type="button"
            variant="gradient"
            size="sm"
            data-font="primary"
            disabled={isResyncing}
            aria-busy={isResyncing}
            className="shrink-0"
            onPointerDownCapture={startResyncIconAnimation}
            onMouseEnter={startResyncIconAnimation}
            onMouseLeave={stopResyncIconAnimation}
            onClick={() => void onResync()}
          >
            <RefreshCCWIcon
              ref={resyncIconRef}
              data-icon="inline-start"
              size={16}
              className="shrink-0 text-current"
              aria-hidden
            />
            <span>{isResyncing ? "Syncing…" : "Resync Trades"}</span>
          </Button>
          ) : null}
          <Button
            type="button"
            variant="outline-brand"
            size="sm"
            data-font="primary"
            className="h-9 shrink-0"
            onPointerDownCapture={startImportIconAnimation}
            onMouseEnter={startImportIconAnimation}
            onMouseLeave={stopImportIconAnimation}
            onClick={onImportTrades}
          >
            <UploadIcon
              ref={importIconRef}
              data-icon="inline-start"
              size={16}
              className="shrink-0 text-current"
              aria-hidden
            />
            <span>Import Trades</span>
          </Button>
        </EmptyContent>
      ) : null}
    </Empty>
  );
}
